import { useI18n } from '../../../i18n';
// ============================================
// FulfillmentVelocityChart - Bar Chart
// Order fulfillment velocity by day of week
// Uses Recharts
// ============================================

import { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { cn } from '../../../utils/cn';

/** Thiết bị cảm ứng: hover không tồn tại, tooltip nổi của recharts bám theo điểm
 *  chạm nên hay lệch khỏi cột vừa bấm (bug #147). */
function useIsTouchDevice(): boolean {
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia('(hover: none), (pointer: coarse)');
    const onChange = () => setIsTouch(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isTouch;
}

// ============================================
// Types
// ============================================

export interface FulfillmentBar {
  day: string; // "Mon", "Tue", etc., or "Avg"
  value: number;
}

interface FulfillmentVelocityChartProps {
  data: FulfillmentBar[];
  className?: string;
}

// ============================================
// Constants
// ============================================

const CHART_COLORS = {
  primary: '#004AC6',
  light: '#ACBFFF',
  accent: '#943700',
};

/**
 * Độ đậm dành cho trạng thái ĐANG CHỌN / ĐANG HOVER. Chỉ đúng một cột được
 * mang giá trị này tại một thời điểm.
 */
const HIGHLIGHT_OPACITY = 1;

/**
 * Bug #175: dải độ đậm mặc định phải DỪNG DƯỚI `HIGHLIGHT_OPACITY`.
 *
 * Trước đây dải là 0.2 → 1.0, nên cột có số đơn cao nhất trong tuần (thực tế
 * hay là 'Wed') luôn được vẽ ở đúng độ đậm của trạng thái "đang chọn". Người
 * dùng bấm sang cột khác thì 'Wed' vẫn đen sì y như cũ — trông như highlight bị
 * kẹt cứng ở thứ Tư, dù state `selectedIndex` vẫn chạy đúng.
 *
 * Không có `day === 'Wed'` hardcode ở đâu cả: lỗi nằm ở chỗ hai trạng thái khác
 * nhau (giá trị lớn nhất / đang được chọn) lại được mã hoá bằng cùng một tín
 * hiệu thị giác. Trần 0.7 giữ nguyên ý nghĩa "cột cao thì đậm hơn" mà vẫn để
 * dành riêng độ đậm tối đa cho cột đang chọn.
 */
const BASE_OPACITY_MIN = 0.2;
const BASE_OPACITY_MAX = 0.7;

function getBarOpacity(value: number, maxValue: number): number {
  if (maxValue === 0) return BASE_OPACITY_MIN;
  const normalized = value / maxValue;
  return BASE_OPACITY_MIN + normalized * (BASE_OPACITY_MAX - BASE_OPACITY_MIN);
}

// ============================================
// Custom Tooltip
// ============================================

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; payload: FulfillmentBar }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: TooltipProps) {
  const { t } = useI18n();
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="rounded-lg bg-white px-3 py-2 shadow-lg ring-1 ring-black/5">
      <p className="text-xs font-bold text-[#191B23]">{label}</p>
      <p className="text-sm text-[#64748B]">
        {payload[0].value} {t('ordersFulfilled')}
      </p>
    </div>
  );
}

// ============================================
// Component
// ============================================

export function FulfillmentVelocityChart({
  data,
  className,
}: FulfillmentVelocityChartProps) {
  const { t } = useI18n();
  const isTouch = useIsTouchDevice();
  // Bug #147: trên mobile không dựa vào tooltip nổi nữa. Bấm vào cột nào thì
  // recharts đưa đúng datum của cột đó qua onClick — ta tự giữ index và hiển thị
  // kết quả ở một khung CỐ ĐỊNH dưới biểu đồ, nên "vùng chọn" luôn khớp cột.
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Dữ liệu đổi (đổi bộ lọc ngày) thì bỏ chọn — tránh trỏ vào cột không còn nữa.
  useEffect(() => {
    setSelectedIndex(null);
  }, [data]);

  const selectedBar = selectedIndex != null ? data[selectedIndex] : undefined;

  // Find max value for opacity calculation
  const maxValue = Math.max(...data.map((d) => d.value), 1);

  return (
    <div
      className={cn(
        'rounded-xl border border-slate-100 bg-white p-6',
        className
      )}
    >
      {/* Header */}
      <div className="mb-6">
        <h3 className="font-sans text-xl font-bold text-[#191B23]">
          {t('orderFulfillmentVelocity')}
        </h3>
        <p className="mt-1 text-sm uppercase tracking-[1.4px] text-[#434655]">
          {t('efficiencyMetrics') || 'Efficiency Metrics'}
        </p>
      </div>

      {/* Legend */}
      <div className="mb-4 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <div
            className="h-3 w-3 rounded-full"
            style={{ backgroundColor: CHART_COLORS.primary }}
          />
          <span className="text-xs text-[#64748B]">{t('ordersFulfilled')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="h-3 w-3 rounded-full"
            style={{ backgroundColor: CHART_COLORS.light }}
          />
          <span className="text-xs text-[#64748B]">{t('processing')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="h-3 w-3 rounded-full"
            style={{ backgroundColor: CHART_COLORS.accent }}
          />
          <span className="text-xs text-[#64748B]">{t('delayed')}</span>
        </div>
      </div>

      {/* Chart.
          `[&_*]:outline-none` + tap-highlight trong suốt: khi chạm vào cột,
          trình duyệt mobile tự vẽ viền focus và một mảng màu highlight lên phần
          tử SVG — đó chính là "khung màu" hiện ra lúc bấm, không phải do recharts. */}
      <div
        className="h-[200px] w-full [&_*]:outline-none [&_*]:focus:outline-none"
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <ResponsiveContainer width="100%" height="100%">
          {/* left margin âm làm vùng hover lệch khỏi cột ngày (bug #124) */}
          <BarChart
            data={data}
            margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
            barCategoryGap="15%"
          >
            <XAxis
              dataKey="day"
              axisLine={false}
              tickLine={false}
              tick={{
                fontSize: 10,
                fontWeight: 700,
                fill: '#94A3B8',
              }}
            />
            <YAxis hide />
            {/* Bug #136: bỏ cursor dạng band (trên mobile khung chọn không bo đúng
                cột ngày) — thay bằng highlight trực tiếp cột đang active.
                Bug #147: trên thiết bị cảm ứng tắt hẳn tooltip nổi, thay bằng
                khung cố định bên dưới. */}
            {!isTouch && <Tooltip content={<CustomTooltip />} cursor={false} />}
            <Bar
              dataKey="value"
              radius={[8, 8, 0, 0]}
              maxBarSize={48}
              // KHÔNG viền: mọi phản hồi khi chọn đều bằng độ đậm của chính cột.
              // Viền quanh cột đang active/selected trên mobile trông như một
              // "khung màu" lạ chồng lên biểu đồ.
              // Hover dùng chung mức đậm với "đang chọn" — cả hai đều là trạng
              // thái tạm thời do người dùng trỏ tới, và chỉ áp cho MỘT cột.
              activeBar={{ fillOpacity: HIGHLIGHT_OPACITY }}
              // Bug #161: bản cũ nhận `index` từ THAM SỐ THỨ HAI của recharts.
              // Khi `<Bar>` có `<Cell>` con, recharts không đảm bảo thứ tự tham
              // số đó — thực tế index luôn về 0, nên bấm cột nào khung chi tiết
              // cũng hiện "MON" và số của thứ Hai (thường là 0).
              //
              // Nay suy ra vị trí từ CHÍNH datum được bấm: `day` là duy nhất
              // trong mảng ('Mon'…'Sun','Avg') nên tra ngược luôn ra đúng cột,
              // không phụ thuộc chữ ký hàm của thư viện.
              onClick={(entry: unknown) => {
                const record = entry as { day?: string; payload?: { day?: string } } | undefined;
                const day = record?.day ?? record?.payload?.day;
                if (!day) return;
                const index = data.findIndex((d) => d.day === day);
                if (index < 0) return;
                setSelectedIndex((current) => (current === index ? null : index));
              }}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={CHART_COLORS.primary}
                  fillOpacity={
                    selectedIndex === index
                      ? HIGHLIGHT_OPACITY
                      : getBarOpacity(entry.value, maxValue)
                  }
                  cursor="pointer"
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Bug #147: khung chi tiết CỐ ĐỊNH — giá trị lấy thẳng từ cột đã bấm nên
          không bao giờ lệch sang ngày khác như tooltip nổi trên mobile. */}
      <div className="mt-3 min-h-[44px] rounded-lg bg-slate-50 px-3 py-2">
        {selectedBar ? (
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-xs font-bold uppercase tracking-wide text-[#191B23]">
              {selectedBar.day}
            </span>
            <span className="text-sm text-[#64748B]">
              {selectedBar.value} {t('ordersFulfilled')}
            </span>
          </div>
        ) : (
          <p className="text-xs text-slate-400">{t('selectBarForDetails')}</p>
        )}
      </div>
    </div>
  );
}
